import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { Shield, CheckCircle } from "lucide-react";

const SetupAdmin = () => {
  const { user, isAdmin, isLoading } = useAuth();
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/auth");
    }
    if (!isLoading && isAdmin) {
      navigate("/admin");
    }
  }, [isLoading, user, isAdmin, navigate]);

  const handleSetupAdmin = async () => {
    if (!user) return;

    setIsSettingUp(true);
    try {
      const { data, error } = await supabase.functions.invoke("make-admin", {
        body: { userId: user.id, email: user.email },
      });

      if (error) throw error;

      if (data.error) {
        toast({
          title: "Cannot grant admin access",
          description: data.error,
          variant: "destructive",
        });
      } else {
        setIsComplete(true);
        toast({ title: "Admin access granted!" });
        // Reload to update auth context
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (error: any) {
      console.error("Error setting up admin:", error);
      toast({
        title: "Setup failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSettingUp(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </Layout>
    );
  }

  if (!user) return null;

  return (
    <>
      <Helmet>
        <title>Setup Admin - Pixie Clicks</title>
      </Helmet>
      <Layout>
        <section className="section-padding">
          <div className="container-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto max-w-md text-center"
            >
              {isComplete ? (
                <>
                  <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
                  <h1 className="mt-6 font-serif text-3xl font-semibold text-foreground">
                    Admin Access Granted!
                  </h1>
                  <p className="mt-4 text-muted-foreground">
                    Redirecting to the admin dashboard...
                  </p>
                </>
              ) : (
                <>
                  <Shield className="mx-auto h-16 w-16 text-primary" />
                  <h1 className="mt-6 font-serif text-3xl font-semibold text-foreground">
                    Setup Admin Access
                  </h1>
                  <p className="mt-4 text-muted-foreground">
                    You're signed in as <strong>{user.email}</strong>. Click below
                    to grant yourself admin privileges.
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Note: Only the first user can self-grant admin access.
                  </p>
                  <Button
                    variant="hero"
                    size="lg"
                    className="mt-8"
                    onClick={handleSetupAdmin}
                    disabled={isSettingUp}
                  >
                    {isSettingUp ? "Setting up..." : "Make Me Admin"}
                  </Button>
                  <p className="mt-6">
                    <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
                      ← Back to home
                    </Link>
                  </p>
                </>
              )}
            </motion.div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default SetupAdmin;
